/**
 * DG Export Service for DG Billing Data
 * Handles CSV export functionality for DG billing records
 */

export class DGExportService {
  /**
   * Exports DG billing records to CSV format
   * @param {Array} records - Array of DG billing records
   * @param {string} filename - Optional filename
   * @returns {void}
   */
  static exportDGRecordsToCSV(records, filename = 'dg_billing_records.csv') {
    try {
      if (!records || records.length === 0) {
        throw new Error('No DG records available for export');
      }

      if (!Array.isArray(records)) {
        throw new Error('Invalid records format - expected an array');
      }

      // Validate filename
      if (!filename || typeof filename !== 'string') {
        filename = 'dg_billing_records.csv';
      }

      // Ensure filename has .csv extension
      if (!filename.toLowerCase().endsWith('.csv')) {
        filename += '.csv';
      }

      // Define CSV headers for DG billing
      const headers = [
        'Record ID',
        'Billing Start Date',
        'Billing End Date',
        'Duration (Days)',
        'Initial DG Reading (kWh)',
        'Final DG Reading (kWh)',
        'Total DG Generation (kWh)',
        'Diesel Consumption (L)',
        'Running Hours',
        'kWh per Litre',
        'Litres per Hour',
        'Average Daily Generation (kWh/day)',
        'Average Hours per Day',
        'Efficiency Rating',
        'Created By',
        'Created Date',
      ];

      // Convert records to CSV rows with error handling
      const csvRows = records.map((record, index) => {
        try {
          // Validate record structure
          if (!record) {
            console.warn(`Skipping null record at index ${index}`);
            return null;
          }

          if (!record.billingPeriod) {
            console.warn(`Record at index ${index} missing billing period`);
            return null;
          }

          const duration = this.calculateBillingDuration(record.billingPeriod);
          const totalConsumption = record.dgConsumptionData?.totalConsumption || 0;
          const avgDailyGeneration = duration > 0
            ? (totalConsumption / duration).toFixed(2)
            : '0.00';
          const avgHoursPerDay = duration > 0
            ? ((record.runningHours || 0) / duration).toFixed(2)
            : '0.00';
          const efficiencyRating = this.getEfficiencyRating(
            record.efficiencyMetrics?.kwhPerLitre || 0,
          );

          return [
            record._id || record.id || `record_${index}`,
            this.formatDateForCSV(record.billingPeriod.startDate),
            this.formatDateForCSV(record.billingPeriod.endDate),
            duration,
            record.dgConsumptionData?.initialReading || 0,
            record.dgConsumptionData?.finalReading || 0,
            totalConsumption,
            record.dieselConsumption || 0,
            record.runningHours || 0,
            (record.efficiencyMetrics?.kwhPerLitre || 0).toFixed(2),
            (record.efficiencyMetrics?.litresPerHour || 0).toFixed(2),
            avgDailyGeneration,
            avgHoursPerDay,
            efficiencyRating,
            record.createdBy || 'operator',
            this.formatDateTimeForCSV(record.createdAt),
          ];
        } catch (error) {
          console.error(`Error processing record at index ${index}:`, error);
          return null;
        }
      }).filter((row) => row !== null); // Remove null rows

      if (csvRows.length === 0) {
        throw new Error('No valid records found for export');
      }

      // Combine headers and rows
      const csvContent = [headers, ...csvRows]
        .map((row) => row.map((field) => `"${this.sanitizeCSVField(field)}"`).join(','))
        .join('\n');

      // Create and download file
      this.downloadFile(csvContent, filename, 'text/csv');
    } catch (error) {
      console.error('Error exporting DG records to CSV:', error);
      throw new Error(`Export failed: ${error.message}`);
    }
  }

  /**
   * Exports a single DG billing record to detailed CSV
   * @param {Object} record - DG billing record
   * @param {string} filename - Optional filename
   * @returns {void}
   */
  static exportDGRecordToCSV(record, filename = null) {
    if (!record) {
      throw new Error('No DG record to export');
    }

    const recordId = record._id || record.id || 'unknown';
    const defaultFilename = `dg_billing_record_${recordId}.csv`;
    const csvContent = this.generateDetailedDGRecordCSV(record);
    this.downloadFile(csvContent, filename || defaultFilename, 'text/csv');
  }

  /**
   * Exports DG billing records summary to CSV
   * @param {Array} records - Array of DG billing records
   * @param {string} filename - Optional filename
   * @returns {void}
   */
  static exportDGSummaryToCSV(records, filename = 'dg_billing_summary.csv') {
    if (!records || records.length === 0) {
      throw new Error('No DG records to export');
    }

    // Calculate summary statistics
    const totalRecords = records.length;
    const totalDGConsumption = records.reduce(
      (sum, r) => sum + (r.dgConsumptionData?.totalConsumption || 0),
      0,
    );
    const totalDieselConsumption = records.reduce(
      (sum, r) => sum + (r.dieselConsumption || 0),
      0,
    );
    const totalRunningHours = records.reduce(
      (sum, r) => sum + (r.runningHours || 0),
      0,
    );

    // Calculate average efficiencies
    const validEfficiencyRecords = records.filter((r) => r.efficiencyMetrics?.kwhPerLitre > 0);
    const avgKwhPerLitre = validEfficiencyRecords.length > 0
      ? validEfficiencyRecords.reduce(
        (sum, r) => sum + r.efficiencyMetrics.kwhPerLitre,
        0,
      ) / validEfficiencyRecords.length
      : 0;
    const avgLitresPerHour = validEfficiencyRecords.length > 0
      ? validEfficiencyRecords.reduce(
        (sum, r) => sum + (r.efficiencyMetrics.litresPerHour || 0),
        0,
      ) / validEfficiencyRecords.length
      : 0;

    // Overall efficiency if we have totals
    const overallKwhPerLitre = totalDieselConsumption > 0
      ? totalDGConsumption / totalDieselConsumption
      : 0;
    const overallLitresPerHour = totalRunningHours > 0
      ? totalDieselConsumption / totalRunningHours
      : 0;

    // Efficiency distribution
    const excellentCount = records.filter(
      (r) => (r.efficiencyMetrics?.kwhPerLitre || 0) >= 3.5,
    ).length;
    const goodCount = records.filter((r) => {
      const eff = r.efficiencyMetrics?.kwhPerLitre || 0;
      return eff >= 3.0 && eff < 3.5;
    }).length;
    const fairCount = records.filter((r) => {
      const eff = r.efficiencyMetrics?.kwhPerLitre || 0;
      return eff >= 2.5 && eff < 3.0;
    }).length;
    const poorCount = records.filter(
      (r) => (r.efficiencyMetrics?.kwhPerLitre || 0) < 2.5,
    ).length;

    const summaryData = [
      ['DG Billing Records Summary', ''],
      ['Generated On', this.formatDateTimeForCSV(new Date())],
      ['', ''],
      ['OVERVIEW', ''],
      ['Total Records', totalRecords],
      [
        'Date Range',
        `${this.formatDateForCSV(records[0].billingPeriod.startDate)} to ${
          this.formatDateForCSV(records[records.length - 1].billingPeriod.endDate)
        }`,
      ],
      ['', ''],
      ['POWER GENERATION SUMMARY', ''],
      ['Total DG Generation (kWh)', totalDGConsumption.toLocaleString()],
      ['Average Generation per Record (kWh)', (totalDGConsumption / totalRecords).toFixed(2)],
      ['', ''],
      ['FUEL CONSUMPTION SUMMARY', ''],
      ['Total Diesel Consumption (L)', totalDieselConsumption.toLocaleString()],
      ['Average Diesel per Record (L)', (totalDieselConsumption / totalRecords).toFixed(2)],
      ['Total Running Hours', totalRunningHours.toLocaleString()],
      ['Average Running Hours per Record', (totalRunningHours / totalRecords).toFixed(2)],
      ['', ''],
      ['EFFICIENCY ANALYSIS', ''],
      ['Overall kWh per Litre', overallKwhPerLitre.toFixed(2)],
      ['Overall Litres per Hour', overallLitresPerHour.toFixed(2)],
      ['Average kWh per Litre (by record)', avgKwhPerLitre.toFixed(2)],
      ['Average Litres per Hour (by record)', avgLitresPerHour.toFixed(2)],
      ['', ''],
      ['EFFICIENCY DISTRIBUTION', ''],
      ['Excellent Performance (≥3.5 kWh/L)', excellentCount],
      ['Good Performance (3.0-3.5 kWh/L)', goodCount],
      ['Fair Performance (2.5-3.0 kWh/L)', fairCount],
      ['Poor Performance (<2.5 kWh/L)', poorCount],
      ['', ''],
      ['PERFORMANCE PERCENTAGES', ''],
      ['Excellent %', ((excellentCount / totalRecords) * 100).toFixed(1)],
      ['Good %', ((goodCount / totalRecords) * 100).toFixed(1)],
      ['Fair %', ((fairCount / totalRecords) * 100).toFixed(1)],
      ['Poor %', ((poorCount / totalRecords) * 100).toFixed(1)],
    ];

    const csvContent = summaryData
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');

    this.downloadFile(csvContent, filename, 'text/csv');
  }

  /**
   * Generates a detailed CSV for a single DG record
   * @param {Object} record - DG billing record
   * @returns {string} CSV content
   */
  static generateDetailedDGRecordCSV(record) {
    const duration = this.calculateBillingDuration(record.billingPeriod);
    const avgDailyGeneration = duration > 0
      ? (record.dgConsumptionData.totalConsumption / duration).toFixed(2)
      : '0.00';
    const avgHoursPerDay = duration > 0
      ? (record.runningHours / duration).toFixed(2)
      : '0.00';
    const efficiencyRating = this.getEfficiencyRating(
      record.efficiencyMetrics?.kwhPerLitre || 0,
    );

    const detailData = [
      ['DG Billing Record Detail', ''],
      ['Record ID', record._id || record.id],
      ['Created By', record.createdBy || 'operator'],
      ['Created Date', this.formatDateTimeForCSV(record.createdAt)],
      ['', ''],
      ['BILLING PERIOD', ''],
      ['Start Date', this.formatDateForCSV(record.billingPeriod.startDate)],
      ['End Date', this.formatDateForCSV(record.billingPeriod.endDate)],
      ['Duration (Days)', duration],
      ['', ''],
      ['DG POWER GENERATION', ''],
      ['Initial Reading (kWh)', record.dgConsumptionData.initialReading || 0],
      ['Final Reading (kWh)', record.dgConsumptionData.finalReading || 0],
      ['Total Generation (kWh)', record.dgConsumptionData.totalConsumption || 0],
      ['Average Daily Generation (kWh/day)', avgDailyGeneration],
      ['', ''],
      ['FUEL & OPERATIONS', ''],
      ['Diesel Consumption (L)', record.dieselConsumption || 0],
      ['Running Hours', record.runningHours || 0],
      ['Average Hours per Day', avgHoursPerDay],
      ['', ''],
      ['EFFICIENCY METRICS', ''],
      ['kWh per Litre', (record.efficiencyMetrics?.kwhPerLitre || 0).toFixed(2)],
      ['Litres per Hour', (record.efficiencyMetrics?.litresPerHour || 0).toFixed(2)],
      ['Efficiency Rating', efficiencyRating],
      ['', ''],
      ['PERFORMANCE ANALYSIS', ''],
      ['Industry Standard Range', '3.0 - 3.5 kWh/L'],
      [
        'Performance vs Standard',
        this.getPerformanceVsStandard(record.efficiencyMetrics?.kwhPerLitre || 0),
      ],
      [
        'Efficiency Analysis',
        this.getEfficiencyAnalysis(record.efficiencyMetrics?.kwhPerLitre || 0),
      ],
    ];

    return detailData
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');
  }

  /**
   * Creates and downloads a file
   * @param {string} content - File content
   * @param {string} filename - File name
   * @param {string} mimeType - MIME type
   * @returns {void}
   */
  static downloadFile(content, filename, mimeType) {
    try {
      if (!content) {
        throw new Error('No content to download');
      }

      if (!filename) {
        throw new Error('Filename is required');
      }

      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw new Error(`Download failed: ${error.message}`);
    }
  }

  /**
   * Sanitizes CSV field content to prevent injection and formatting issues
   * @param {any} field - Field value to sanitize
   * @returns {string} Sanitized field value
   */
  static sanitizeCSVField(field) {
    if (field === null || field === undefined) {
      return '';
    }

    // Convert to string and handle special characters
    let sanitized = String(field);

    // Remove or escape potentially dangerous characters
    sanitized = sanitized.replace(/"/g, '""'); // Escape quotes
    sanitized = sanitized.replace(/[\r\n]/g, ' '); // Replace line breaks with spaces

    return sanitized;
  }

  /**
   * Helper methods for formatting and calculations
   */
  static formatDateForCSV(date) {
    return new Date(date).toLocaleDateString('en-US');
  }

  static formatDateTimeForCSV(date) {
    return new Date(date).toLocaleString('en-US');
  }

  static calculateBillingDuration(period) {
    const start = new Date(period.startDate);
    const end = new Date(period.endDate);
    const diffTime = Math.abs(end - start);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }

  static getEfficiencyRating(kwhPerLitre) {
    if (kwhPerLitre >= 3.5) return 'Excellent';
    if (kwhPerLitre >= 3.0) return 'Good';
    if (kwhPerLitre >= 2.5) return 'Fair';
    return 'Poor';
  }

  static getPerformanceVsStandard(kwhPerLitre) {
    const industryStandard = 3.25; // Mid-point of 3.0-3.5 range
    const gap = kwhPerLitre - industryStandard;
    if (gap > 0) {
      return `Above standard by ${gap.toFixed(2)} kWh/L`;
    } if (gap < 0) {
      return `Below standard by ${Math.abs(gap).toFixed(2)} kWh/L`;
    }
    return 'Meets standard';
  }

  static getEfficiencyAnalysis(kwhPerLitre) {
    if (kwhPerLitre >= 3.5) {
      return 'Excellent efficiency above industry standards. '
        + 'Optimal fuel utilization and well-maintained equipment.';
    }
    if (kwhPerLitre >= 3.0) {
      return 'Good efficiency within industry standards. '
        + 'Performing well with room for minor optimization.';
    }
    if (kwhPerLitre >= 2.5) {
      return 'Fair efficiency below optimal range. '
        + 'Consider maintenance checks or load optimization.';
    }
    return 'Poor efficiency significantly below standards. Immediate attention required.';
  }
}

/**
 * DG Search Service for DG Billing Records
 * Provides advanced search and filtering capabilities for DG records
 */
export const DGSearchService = {
  /**
   * Performs advanced search on DG billing records
   * @param {Array} records - Array of DG billing records
   * @param {Object} searchCriteria - Search criteria
   * @returns {Array} Filtered records
   */
  searchDGRecords(records, searchCriteria) {
    if (!records || records.length === 0) {
      return [];
    }

    let filtered = [...records];

    // Text search across multiple fields
    if (searchCriteria.searchText) {
      const searchText = searchCriteria.searchText.toLowerCase();
      filtered = filtered.filter((record) => (
        (record.id && record.id.toLowerCase().includes(searchText))
          || (record._id && record._id.toLowerCase().includes(searchText))
          || (record.createdBy && record.createdBy.toLowerCase().includes(searchText))
          || (record.dieselConsumption && record.dieselConsumption.toString().includes(searchText))
          || (record.runningHours && record.runningHours.toString().includes(searchText))
          || (record.dgConsumptionData?.totalConsumption
            && record.dgConsumptionData.totalConsumption.toString().includes(searchText))
          || (record.efficiencyMetrics?.kwhPerLitre
            && record.efficiencyMetrics.kwhPerLitre.toString().includes(searchText))
      ));
    }

    // Date range filters
    if (searchCriteria.startDate) {
      const startDate = new Date(searchCriteria.startDate);
      filtered = filtered.filter((record) => {
        const recordDate = new Date(record.billingPeriod.startDate);
        return recordDate >= startDate;
      });
    }

    if (searchCriteria.endDate) {
      const endDate = new Date(searchCriteria.endDate);
      filtered = filtered.filter((record) => {
        const recordDate = new Date(record.billingPeriod.endDate);
        return recordDate <= endDate;
      });
    }

    // Efficiency range filters
    if (searchCriteria.minEfficiency !== null && searchCriteria.minEfficiency !== undefined) {
      const minEfficiency = parseFloat(searchCriteria.minEfficiency);
      filtered = filtered.filter((record) => (
        (record.efficiencyMetrics?.kwhPerLitre || 0) >= minEfficiency
      ));
    }

    if (searchCriteria.maxEfficiency !== null && searchCriteria.maxEfficiency !== undefined) {
      const maxEfficiency = parseFloat(searchCriteria.maxEfficiency);
      filtered = filtered.filter((record) => (
        (record.efficiencyMetrics?.kwhPerLitre || 0) <= maxEfficiency
      ));
    }

    // Diesel consumption filters
    if (
      searchCriteria.minDieselConsumption !== null
      && searchCriteria.minDieselConsumption !== undefined
    ) {
      const minDiesel = parseFloat(searchCriteria.minDieselConsumption);
      filtered = filtered.filter((record) => (
        (record.dieselConsumption || 0) >= minDiesel
      ));
    }

    // Running hours filters
    if (searchCriteria.minRunningHours !== null && searchCriteria.minRunningHours !== undefined) {
      const minHours = parseFloat(searchCriteria.minRunningHours);
      filtered = filtered.filter((record) => (record.runningHours || 0) >= minHours);
    }

    if (searchCriteria.maxRunningHours !== null && searchCriteria.maxRunningHours !== undefined) {
      const maxHours = parseFloat(searchCriteria.maxRunningHours);
      filtered = filtered.filter((record) => (record.runningHours || 0) <= maxHours);
    }

    // Efficiency rating filter
    if (searchCriteria.efficiencyRating) {
      filtered = filtered.filter((record) => {
        const efficiency = record.efficiencyMetrics?.kwhPerLitre || 0;
        const rating = DGExportService.getEfficiencyRating(efficiency);
        return rating.toLowerCase() === searchCriteria.efficiencyRating.toLowerCase();
      });
    }

    // Created by filter
    if (searchCriteria.createdBy) {
      const createdBy = searchCriteria.createdBy.toLowerCase();
      filtered = filtered.filter((record) => (
        record.createdBy && record.createdBy.toLowerCase().includes(createdBy)
      ));
    }

    // Sort results
    if (searchCriteria.sortBy) {
      filtered = DGSearchService.sortDGRecords(
        filtered,
        searchCriteria.sortBy,
        searchCriteria.sortDesc,
      );
    }

    return filtered;
  },

  /**
   * Sorts DG records by specified field
   * @param {Array} records - Records to sort
   * @param {string} sortBy - Field to sort by
   * @param {boolean} sortDesc - Sort in descending order
   * @returns {Array} Sorted records
   */
  sortDGRecords(records, sortBy, sortDesc = false) {
    const sorted = [...records].sort((a, b) => {
      let aVal;
      let bVal;

      switch (sortBy) {
        case 'billingPeriod':
          aVal = new Date(a.billingPeriod.startDate);
          bVal = new Date(b.billingPeriod.startDate);
          break;
        case 'dgConsumption':
          aVal = a.dgConsumptionData?.totalConsumption || 0;
          bVal = b.dgConsumptionData?.totalConsumption || 0;
          break;
        case 'dieselConsumption':
          aVal = a.dieselConsumption || 0;
          bVal = b.dieselConsumption || 0;
          break;
        case 'runningHours':
          aVal = a.runningHours || 0;
          bVal = b.runningHours || 0;
          break;
        case 'kwhPerLitre':
          aVal = a.efficiencyMetrics?.kwhPerLitre || 0;
          bVal = b.efficiencyMetrics?.kwhPerLitre || 0;
          break;
        case 'litresPerHour':
          aVal = a.efficiencyMetrics?.litresPerHour || 0;
          bVal = b.efficiencyMetrics?.litresPerHour || 0;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt);
          bVal = new Date(b.createdAt);
          break;
        default:
          aVal = a[sortBy];
          bVal = b[sortBy];
      }

      if (aVal < bVal) return sortDesc ? 1 : -1;
      if (aVal > bVal) return sortDesc ? -1 : 1;
      return 0;
    });

    return sorted;
  },

  /**
   * Gets search suggestions based on existing DG records
   * @param {Array} records - Array of DG billing records
   * @param {string} field - Field to get suggestions for
   * @returns {Array} Array of suggestions
   */
  getDGSearchSuggestions(records, field) {
    if (!records || records.length === 0) {
      return [];
    }

    const suggestions = new Set();

    records.forEach((record) => {
      switch (field) {
        case 'createdBy':
          if (record.createdBy) {
            suggestions.add(record.createdBy);
          }
          break;
        case 'dieselConsumption':
          if (record.dieselConsumption) {
            suggestions.add(record.dieselConsumption);
          }
          break;
        case 'runningHours':
          if (record.runningHours) {
            suggestions.add(record.runningHours);
          }
          break;
        case 'efficiency':
          if (record.efficiencyMetrics?.kwhPerLitre) {
            suggestions.add(record.efficiencyMetrics.kwhPerLitre.toFixed(2));
          }
          break;
        default:
          break;
      }
    });

    return Array.from(suggestions).sort();
  },
};

export default {
  DGExportService,
  DGSearchService,
};
